import { createColumnHelper } from "@tanstack/react-table";
import { T } from "@tolgee/react";
import { ExternalLink } from "lucide-react";
import type { UserFill } from "#/hooks/use-hyperliquid-account";
import { getHyperliquidExplorerTxUrl } from "#/lib/explorer";
import {
	formatClockTime,
	formatPrice,
	formatSize,
	formatUsd,
	toNumber,
} from "#/lib/formatters";

const col = createColumnHelper<UserFill>();

export const recentTradesColumns = [
	col.accessor("time", {
		header: () => <T keyName="portfolio.time" />,
		cell: ({ row, getValue }) => {
			const { hash } = row.original;
			const formatted = formatClockTime(getValue());
			if (!hash) {
				return (
					<span className="text-label2-tabular-medium text-muted-foreground">
						{formatted}
					</span>
				);
			}
			return (
				<a
					href={getHyperliquidExplorerTxUrl(hash)}
					target="_blank"
					rel="noreferrer"
					aria-label="View transaction on Hyperliquid Explorer"
					className="inline-flex items-center gap-1 text-label2-tabular-medium text-muted-foreground transition-colors hover:text-foreground"
				>
					{formatted}
					<ExternalLink className="h-3 w-3" />
				</a>
			);
		},
	}),
	col.accessor("coin", {
		header: () => <T keyName="portfolio.market" />,
		cell: ({ getValue }) => (
			<>
				<span className="text-label2-medium hidden sm:block">{getValue()}-PERP</span>
				<span className="text-label2-medium sm:hidden">{getValue()}</span>
			</>
		),
	}),
	col.accessor("side", {
		header: () => <T keyName="portfolio.side" />,
		cell: ({ getValue }) => {
			const isBuy = getValue() === "B";
			return (
				<span
					className={`text-label2-medium ${isBuy ? "text-trade-green" : "text-trade-red"}`}
				>
					{isBuy ? <T keyName="general.buy" /> : <T keyName="general.sell" />}
				</span>
			);
		},
	}),
	col.accessor("sz", {
		header: () => (
			<span className="block text-right">
				<T keyName="portfolio.size" />
			</span>
		),
		cell: ({ getValue }) => (
			<span className="block text-right text-label2-tabular-medium">
				{formatSize(toNumber(getValue()))}
			</span>
		),
	}),
	col.accessor("px", {
		header: () => (
			<span className="block text-right">
				<T keyName="portfolio.price" />
			</span>
		),
		cell: ({ getValue }) => (
			<span className="block text-right text-label2-tabular-medium">
				{formatPrice(getValue())}
			</span>
		),
	}),
	col.accessor("fee", {
		header: () => (
			<span className="block text-right">
				<T keyName="portfolio.fee" />
			</span>
		),
		cell: ({ getValue }) => (
			<span className="block text-right text-label2-tabular-medium text-muted-foreground">
				{formatUsd(toNumber(getValue()))}
			</span>
		),
	}),
];
